/**
 * Transactions API - Endpoints for transaction management
 * Connected to transaction-service via gateway
 */
import apiClient from './client';

// ============================================
// TYPES - Transaction
// ============================================

export type TransactionType =
    | 'depo_sol'
    | 'depo_bs'
    | 'depo_mutuelle'
    | 'depo_solide'
    | 'retrait_sol'
    | 'retrait_bs'
    | 'retrait_mutuelle'
    | 'retrait_placement'
    | 'placement'
    | 'mutuelle'
    | 'pret'
    | 'transfer'
    | 'rembousement'
    | 'paiement'
    | 'paiement_service'
    | 'commission_solide';

export type TransactionStatus = 'en_cours' | 'reussi' | 'echoue' | 'annule';

export interface Transaction {
    id: string;
    compte_id: string | null;
    compte_code: string | null;
    client_nom: string | null;
    type: TransactionType;
    montant: string; // Decimal from backend, comes as string
    status: TransactionStatus;
    description: string | null;
    moyen_paiement: string;
    reference_externe: string | null;
    created_by_id: string;
    created_by_username: string | null;
    created_at: string;
    updated_at: string;
    date_transaction: string;
    date_sol: string | null;
}

export type ReviewStatus = 'pending' | 'approved' | 'rejected';
export type RestrictionType = 'amount_bs' | 'amount_sol' | 'time_cutoff';

export interface TransactionReview {
    id: string;
    restriction_type: RestrictionType;
    restriction_type_display: string;
    montant: string;
    agent_id: string;
    agent_name: string;
    client_name: string | null;
    compte_code: string;
    status: ReviewStatus;
    status_display: string;
    admin_notes: string | null;
    created_at: string;
    processed_at: string | null;
    processed_by_name: string | null;
}

// Grouped transaction item (for depo_sol grouping)
export interface GroupedTransactionItem {
    is_group: boolean;
    // Common fields
    compte_id?: string | null;
    compte_code?: string | null;
    client_nom?: string | null;
    // For single transactions
    id?: string;
    type: TransactionType;
    montant?: string;
    status?: TransactionStatus;
    created_at?: string;
    date_sol?: string | null;
    description?: string | null;
    created_by_username?: string | null;
    // For grouped transactions
    group_key?: string;
    total_montant?: string;
    count?: number;
    latest_created_at?: string;
    transaction_ids?: string[];
    transactions?: Array<{
        id: string;
        montant: string;
        status: TransactionStatus;
        created_at: string;
        date_sol?: string | null;
        description: string | null;
        created_by_username?: string | null;
    }>;
}

export interface TransactionsListResponse {
    count: number;
    next: string | null;
    previous: string | null;
    results: Transaction[];
}

export interface TransactionFilters {
    compte_id?: string;
    type?: TransactionType;
    status?: TransactionStatus;
    search?: string;
    date_debut?: string; // YYYY-MM-DD
    date_fin?: string; // YYYY-MM-DD
    page?: number;
    page_size?: number;
    group_sol?: boolean; // Enable SOL grouping
    created_by_id?: string; // Admin only: filtre pa userId ajan
}

export interface CancelTransactionRequest {
    transaction_id: string;
    reason?: string;
}

export interface DepositRequest {
    compte_id: string;
    compte_code?: string;
    montant: number;
    locale?: string;
}

export interface WithdrawalRequest {
    compte_id: string;
    compte_code?: string;
    montant: number;
    locale?: string;
}

export type WithdrawalRequestStatus = 'pending' | 'approved' | 'rejected';

export interface WithdrawalRequestModel {
    id: string;
    agent_id: string;
    agent_name: string;
    compte_id: string;
    compte_code: string;
    client_name: string;
    montant: string;
    devise: string;
    status: WithdrawalRequestStatus;
    reviewed_by: string | null;
    reviewed_at: string | null;
    rejection_reason: string | null;
    transaction_id: string | null;
    initiated_at: string;
    updated_at?: string;
}

export interface DepositResponse {
    message: string;
    transaction_id: string;
    montant: number;
    nouveau_solde_client: number;
    pdf_generated: boolean;
    agent_deducted: boolean;
    nouveau_solde_agent?: number;
}

export interface WithdrawalResponse {
    message: string;
    transaction_id: string;
    montant: number;
    frais_total: number;
    frais_systeme: number;
    nouveau_solde_client: number;
    pdf_generated: boolean;
    commission_agent?: number;
    nouveau_solde_agent?: number;
}

export interface DailyStats {
    date: string;
    counts: {
        depo_bs: number;
        retrait_bs: number;
        depo_sol: number;
        total: number;
    };
    totals: {
        depo_bs: number;
        retrait_bs: number;
        depo_sol: number;
    };
}

// ============================================
// API FUNCTIONS
// ============================================

export const transactionsApi = {
    /**
     * List all transactions (paginated with filters)
     */
    async list(filters?: TransactionFilters): Promise<TransactionsListResponse> {
        const response = await apiClient.get('/api/transactions/', { params: filters });
        return response.data;
    },

    /**
     * Get transaction by ID
     */
    async getById(id: string): Promise<Transaction> {
        const response = await apiClient.get(`/api/transactions/${id}/`);
        return response.data;
    },

    /**
     * Get transactions for a specific compte (with SOL grouping by default)
     */
    async getByCompte(compteId: string, options?: {
        groupSol?: boolean;
        limit?: number;
    }): Promise<GroupedTransactionItem[]> {
        const response = await apiClient.get('/api/transactions/details/', {
            params: {
                compte_id: compteId,
                group_sol: options?.groupSol !== false ? 'true' : 'false',
                limit: options?.limit
            }
        });
        return response.data;
    },

    /**
     * Cancel a transaction (superuser only)
     */
    async cancel(data: CancelTransactionRequest): Promise<{ message: string; log_id: string }> {
        const response = await apiClient.post('/api/transactions/cancel/', data);
        return response.data;
    },

    /**
     * Cancel BS transaction by ID
     */
    async cancelBs(transactionId: string, reason?: string): Promise<{ message: string; log_id: string }> {
        const response = await apiClient.post(`/api/transactions/cancel-bs/${transactionId}/`, { reason });
        return response.data;
    },

    /**
     * Agent deposit (add to client account)
     */
    async deposit(data: DepositRequest): Promise<DepositResponse> {
        const response = await apiClient.post('/api/transactions/depot-agent/', data);
        return response.data;
    },

    /**
     * Agent SOL deposit
     */
    async depositSol(data: DepositRequest): Promise<DepositResponse> {
        const response = await apiClient.post('/api/transactions/depo-sol/', data);
        return response.data;
    },

    /**
     * Agent withdrawal (from client account)
     */
    async withdraw(data: WithdrawalRequest): Promise<WithdrawalResponse> {
        const response = await apiClient.post('/api/transactions/retrait-agent/', data);
        return response.data;
    },

    /**
     * Agent SOL withdrawal
     */
    async withdrawSol(data: WithdrawalRequest): Promise<WithdrawalResponse> {
        const response = await apiClient.post('/api/transactions/retrait-sol/', data);
        return response.data;
    },

    /**
     * Get daily agent stats
     */
    async getDailyStats(date?: string): Promise<DailyStats> {
        const response = await apiClient.get('/api/transactions/stats/daily/', {
            params: date ? { date } : undefined
        });
        return response.data;
    },

    /**
     * Get user transaction stats
     */
    async getUserStats(): Promise<{
        totals: { deposits: number; withdrawals: number; sol: number };
        counts: { deposits: number; withdrawals: number; sol: number; total: number };
    }> {
        const response = await apiClient.get('/api/transactions/user-transactions-stats/');
        return response.data;
    },

    /**
     * Get transaction history (for current user)
     */
    async getHistory(params?: { page?: number; page_size?: number }): Promise<TransactionsListResponse> {
        const response = await apiClient.get('/api/transactions/history/', { params });
        return response.data;
    },

    /**
     * Get fee config for a transaction type
     */
    async getFeeConfig(transactionType: string): Promise<FeeConfig | null> {
        try {
            const response = await apiClient.get(`/api/transactions/config/fees/${transactionType}/`);
            return response.data;
        } catch (error) {
            console.warn(`Fee config not found for ${transactionType}, using default.`);
            return null;
        }
    },

    /**
     * List all fee configs
     */
    async listFeeConfigs(): Promise<FeeConfig[]> {
        try {
            const response = await apiClient.get('/api/transactions/config/fees/');
            return response.data;
        } catch {
            return [];
        }
    },
    /**
     * Payer un service interne (ex: Solidaire) avec un solde BS
     */
    async payService(data: {
        compte_bs_id?: string;
        compte_bs_code?: string;
        compte_sol_id?: string;
        compte_id?: string; // Alternative for destination
        montant: number;
    }) {
        const response = await apiClient.post('/api/transactions/payment-service/', data);
        return response.data;
    },

    /**
     * Create PayPal order for deposit
     */
    async createPayPalOrder(data: {
        compte: string;
        montant: number;
        description?: string;
    }): Promise<{ approval_url: string; order_id: string }> {
        const response = await apiClient.post('/api/transactions/paypal/create-order/', data);
        return response.data;
    },

    /**
     * List transactions pending review
     */
    async listReviews(params?: { status?: ReviewStatus; page?: number }): Promise<{
        count: number;
        results: TransactionReview[];
        next: string | null;
        previous: string | null;
    }> {
        const response = await apiClient.get('/api/transactions/reviews/', { params });
        return response.data;
    },

    /**
     * Approve or reject a transaction review
     */
    async actionReview(id: string, action: 'approve' | 'reject', notes?: string): Promise<{ message: string; transaction_id?: string }> {
        const response = await apiClient.post(`/api/transactions/reviews/${id}/action/`, { action, admin_notes: notes });
        return response.data;
    },

    /**
     * List withdrawal requests (admin: all; agent: own)
     */
    async getWithdrawalRequests(params?: { status?: WithdrawalRequestStatus; agent_id?: string; page?: number }): Promise<{
        count: number;
        requests: WithdrawalRequestModel[];
    }> {
        const response = await apiClient.get('/api/transactions/retrait-requests/pending/', { params });
        return response.data;
    },

    /**
     * Approve a withdrawal request
     */
    async approveWithdrawalRequest(requestId: string): Promise<{ message: string; transaction_id?: string }> {
        const response = await apiClient.post(`/api/transactions/retrait-requests/${requestId}/approve/`);
        return response.data;
    },

    /**
     * Reject a withdrawal request
     */
    async rejectWithdrawalRequest(requestId: string, reason?: string): Promise<{ message: string }> {
        const response = await apiClient.post(`/api/transactions/retrait-requests/${requestId}/reject/`, { rejection_reason: reason });
        return response.data;
    },
};

// Fee configuration interface
export interface FeeConfig {
    id: number;
    transaction_type: string;
    transaction_type_display: string;
    percentage: string; // Decimal as string
    is_active: boolean;
    description: string | null;
    created_at: string;
    updated_at: string;
}

export default transactionsApi;
